/**
 * Where a transition is drawn. No React, no DOM.
 *
 * FLOATING rather than tied to handles: an edge leaves its source at the point
 * on the box's border facing the target. The fixed right-to-left handles React
 * Flow defaults to sent a return transition back THROUGH both boxes, where the
 * boxes hid it - one of the two edges of a two-state loop was simply invisible.
 *
 * Several edges between one pair of states - a loop's two directions, or two
 * triggers the same way - are bowed apart by an offset, so none lies on another.
 */

import type { LayoutBox } from '../../schema'

export interface Point {
  x: number
  y: number
}

export interface EdgeShape {
  /** An SVG path `d`. */
  path: string
  label: Point
}

/** How far apart parallel edges bow, in diagram units. */
export const PARALLEL_SPACING = 28

const centre = (box: LayoutBox): Point => ({ x: box.x + box.width / 2, y: box.y + box.height / 2 })

/**
 * Where the ray from a box's centre towards a point leaves the box.
 *
 * A point inside the box, or at its centre, answers the centre: there is no
 * border between them to meet.
 */
export function borderPoint(box: LayoutBox, towards: Point): Point {
  const c = centre(box)
  const dx = towards.x - c.x
  const dy = towards.y - c.y
  if (dx === 0 && dy === 0) return c
  const scale = Math.min(
    dx === 0 ? Number.POSITIVE_INFINITY : box.width / 2 / Math.abs(dx),
    dy === 0 ? Number.POSITIVE_INFINITY : box.height / 2 / Math.abs(dy),
  )
  return scale >= 1 ? c : { x: c.x + dx * scale, y: c.y + dy * scale }
}

/**
 * The offset each edge gets, by edge id, so edges between one pair of boxes fan out.
 *
 * Grouped by the UNORDERED pair, and signed against a canonical direction (the
 * lower id first), so a transition and its reverse bow to opposite sides rather
 * than both to their own left - which would put them on the same line again.
 */
export function parallelOffsets(
  edges: { id: string; source: string; target: string }[],
): Record<string, number> {
  const groups = new Map<string, { id: string; forward: boolean }[]>()
  for (const edge of edges) {
    if (edge.source === edge.target) continue
    const forward = edge.source < edge.target
    const key = forward ? `${edge.source} ${edge.target}` : `${edge.target} ${edge.source}`
    const group = groups.get(key) ?? []
    group.push({ id: edge.id, forward })
    groups.set(key, group)
  }
  const offsets: Record<string, number> = {}
  for (const group of groups.values()) {
    if (group.length === 1) {
      offsets[group[0].id] = 0
      continue
    }
    group.forEach((entry, index) => {
      const lane = (index - (group.length - 1) / 2) * PARALLEL_SPACING
      // Stated against the canonical direction, then turned to the edge's own.
      offsets[entry.id] = entry.forward ? lane : -lane
    })
  }
  return offsets
}

/**
 * A transition from one box to another, bowed by `offset` to its left.
 *
 * A quadratic through a control point off the midpoint, with each end on the
 * border facing that control point - so a bowed edge still meets its boxes
 * square-on rather than starting at a point hidden under the box.
 */
export function transitionShape(source: LayoutBox, target: LayoutBox, offset = 0): EdgeShape {
  const from = centre(source)
  const to = centre(target)
  const dx = to.x - from.x
  const dy = to.y - from.y
  const length = Math.hypot(dx, dy) || 1
  // The left-hand normal of the direction of travel.
  const nx = -dy / length
  const ny = dx / length
  const control = { x: (from.x + to.x) / 2 + nx * offset * 2, y: (from.y + to.y) / 2 + ny * offset * 2 }
  const start = borderPoint(source, control)
  const end = borderPoint(target, control)
  return {
    path: `M ${round(start.x)} ${round(start.y)} Q ${round(control.x)} ${round(control.y)} ${round(end.x)} ${round(end.y)}`,
    // The curve's own midpoint, which for a quadratic is halfway to the control.
    label: { x: round((start.x + 2 * control.x + end.x) / 4), y: round((start.y + 2 * control.y + end.y) / 4) },
  }
}

/** A transition from a state to itself: a loop off its top-right corner. */
export function selfLoopShape(box: LayoutBox): EdgeShape {
  const right = box.x + box.width
  const start = { x: right - 16, y: box.y }
  const end = { x: right, y: box.y + 16 }
  return {
    path: `M ${start.x} ${start.y} C ${start.x} ${box.y - 40}, ${right + 40} ${end.y}, ${end.x} ${end.y}`,
    label: { x: right + 18, y: box.y - 18 },
  }
}

const round = (value: number) => Math.round(value * 10) / 10
